import type { CurrentOrganisation, Device, FilterInput, Node as GqlNode, User } from "generated/graphql";
import { nameOf } from "src/helpers";
import { create } from "zustand";
// -----------------------------------------------------------------------------
// Shared helpers
// -----------------------------------------------------------------------------

export type Updater<T> = T | ((prev: T) => T);

const applyUpdater = <T>(next: Updater<T>, prev: T): T =>
	typeof next === "function" ? (next as (p: T) => T)(prev) : next;

// Same default as your for devicesFilterState
const DEFAULT_DEVICES_FILTER: FilterInput = {
	pagination: { current: 1, pageSize: 20 },
	sorts: { field: nameOf<Device>("name"), order: "ascend" }
};

// -----------------------------------------------------------------------------
// Slice types
// -----------------------------------------------------------------------------

export type ThemeMode = "light" | "dark" | "system";

// Settings
export type SettingsState = {
	isDirty: boolean;
	setIsDirty: (value: Updater<boolean>) => void;

	settingsLocale: string;
	setSettingsLocale: (value: Updater<string>) => void;

	selectedNode?: GqlNode;
	setSelectedNode: (value: Updater<GqlNode | undefined>) => void;

	rootNode?: GqlNode;
	setRootNode: (value: Updater<GqlNode | undefined>) => void;

	currentUser?: User;
	setCurrentUser: (value: Updater<User | undefined>) => void;

	currentOrg?: CurrentOrganisation;
	setCurrentOrg: (value: Updater<CurrentOrganisation | undefined>) => void;

	currentRoles: Set<string>;
	setCurrentRoles: (value: Updater<Set<string>>) => void;

	themeMode: ThemeMode;
	setThemeMode: (value: Updater<ThemeMode>) => void;

	// atomFamily<FilterInput, number> → map by nodeId
	devicesFilters: Record<number, FilterInput>;
	setDevicesFilter: (nodeId: number, value: Updater<FilterInput>) => void;
};

// Auth
export type AuthState = {
	authIsLoading: boolean;
	setAuthIsLoading: (value: Updater<boolean>) => void;

	authIsAuthenticated: boolean;
	setAuthIsAuthenticated: (value: Updater<boolean>) => void;

	authRedirectUrl?: string;
	setAuthRedirectUrl: (value: Updater<string | undefined>) => void;
};

// Tenant
export type TenantState = {
	tenantId: string;
	setTenantId: (value: Updater<string>) => void;

	tenantName: string;
	setTenantName: (value: Updater<string>) => void;
};

// Company
export type CompanyState = {
	companyId: string;
	setCompanyId: (value: Updater<string>) => void;
};

// Upload
export type UploadState = {
	uploadFinished: boolean;
	setUploadFinished: (value: Updater<boolean>) => void;

	uploadedFile: UploadedFile | null;
	setUploadedFile: (value: Updater<UploadedFile | null>) => void;
};

export type UploadedFile = {
	fileId?: string | null;
	[key: string]: any;
};

// General
type BusinessDomain = any;

export type GeneralState = {
	businessDomains: BusinessDomain[];
	setBusinessDomains: (value: Updater<BusinessDomain[]>) => void;

	defaultBusinessDomains: BusinessDomain[];
	setDefaultBusinessDomains: (value: Updater<BusinessDomain[]>) => void;

	pendingForwardersParentId?: number | null;
	setPendingForwardersParentId: (value: Updater<number | null | undefined>) => void;
};

// -----------------------------------------------------------------------------
// Root AppState
// -----------------------------------------------------------------------------

export type AppState = SettingsState & AuthState & TenantState & CompanyState & UploadState & GeneralState;

// -----------------------------------------------------------------------------
// Store implementation
// -----------------------------------------------------------------------------

export const useAppStore = create<AppState>(set => ({
	// ----- SettingsState -------------------------------------------------------
	isDirty: false,
	setIsDirty: value =>
		set(state => ({
			isDirty: applyUpdater(value, state.isDirty)
		})),

	settingsLocale: "en_GB",
	setSettingsLocale: value =>
		set(state => ({
			settingsLocale: applyUpdater(value, state.settingsLocale)
		})),

	selectedNode: undefined,
	setSelectedNode: value =>
		set(state => ({
			selectedNode: applyUpdater(value, state.selectedNode)
		})),

	rootNode: undefined,
	setRootNode: value =>
		set(state => ({
			rootNode: applyUpdater(value, state.rootNode)
		})),

	currentUser: undefined,
	setCurrentUser: value =>
		set(state => ({
			currentUser: applyUpdater(value, state.currentUser)
		})),

	currentOrg: undefined,
	setCurrentOrg: value =>
		set(state => ({
			currentOrg: applyUpdater(value, state.currentOrg)
		})),

	currentRoles: new Set<string>([]),
	setCurrentRoles: value =>
		set(state => ({
			currentRoles: applyUpdater(value, state.currentRoles)
		})),

	themeMode: "light",
	setThemeMode: value =>
		set(state => ({
			themeMode: applyUpdater(value, state.themeMode)
		})),

	devicesFilters: {},
	setDevicesFilter: (nodeId, updater) =>
		set(state => {
			const prev = state.devicesFilters[nodeId] ?? DEFAULT_DEVICES_FILTER;
			const next = applyUpdater(updater, prev);
			if (Object.is(prev, next)) return state;

			return {
				devicesFilters: {
					...state.devicesFilters,
					[nodeId]: next
				}
			};
		}),

	// ----- AuthState -----------------------------------------------------------
	authIsLoading: true,
	setAuthIsLoading: value =>
		set(state => ({
			authIsLoading: applyUpdater(value, state.authIsLoading)
		})),

	authIsAuthenticated: false,
	setAuthIsAuthenticated: value =>
		set(state => ({
			authIsAuthenticated: applyUpdater(value, state.authIsAuthenticated)
		})),

	authRedirectUrl: undefined,
	setAuthRedirectUrl: value =>
		set(state => ({
			authRedirectUrl: applyUpdater(value, state.authRedirectUrl)
		})),

	// ----- TenantState ---------------------------------------------------------
	tenantId: "",
	setTenantId: value =>
		set(state => ({
			tenantId: applyUpdater(value, state.tenantId)
		})),

	tenantName: "",
	setTenantName: value =>
		set(state => ({
			tenantName: applyUpdater(value, state.tenantName)
		})),

	// ----- CompanyState --------------------------------------------------------
	companyId: "",
	setCompanyId: value =>
		set(state => ({
			companyId: applyUpdater(value, state.companyId)
		})),

	// ----- UploadState ---------------------------------------------------------
	uploadFinished: false,
	setUploadFinished: value =>
		set(state => ({
			uploadFinished: applyUpdater(value, state.uploadFinished)
		})),

	uploadedFile: null,
	setUploadedFile: value =>
		set(state => ({
			uploadedFile: applyUpdater(value, state.uploadedFile)
		})),

	// ----- GeneralState --------------------------------------------------------
	businessDomains: [],
	setBusinessDomains: value =>
		set(state => ({
			businessDomains: applyUpdater(value, state.businessDomains)
		})),

	defaultBusinessDomains: [],
	setDefaultBusinessDomains: value =>
		set(state => ({
			defaultBusinessDomains: applyUpdater(value, state.defaultBusinessDomains)
		})),

	pendingForwardersParentId: undefined,
	setPendingForwardersParentId: value =>
		set(state => ({
			pendingForwardersParentId: applyUpdater(value, state.pendingForwardersParentId)
		}))
}));

export const useThemeMode = () => {
	const themeMode = useAppStore(s => s.themeMode);
	const setThemeMode = useAppStore(s => s.setThemeMode);
	return { themeMode, setThemeMode };
};

export const useDevicesFilterState = (nodeId: number): [FilterInput, (value: Updater<FilterInput>) => void] => {
	const filter = useAppStore(s => s.devicesFilters[nodeId] ?? DEFAULT_DEVICES_FILTER);
	const setDevicesFilter = useAppStore(s => s.setDevicesFilter);

	const setFilter = (updater: Updater<FilterInput>) => {
		setDevicesFilter(nodeId, updater);
	};

	return [filter, setFilter];
};